import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to dynamically set auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Add response interceptor to handle auth errors
api.interceptors.response.use((response) => {
  return response;
}, (error) => {
  console.error('Admin API Error:', {
    status: error.response?.status,
    data: error.response?.data,
    message: error.message,
    url: error.config?.url
  });
  
  if (error.response?.status === 401) {
    // Token expired or invalid, redirect to login
    localStorage.removeItem('access_token');
    window.location.href = '/login';
  } else if (error.response?.status === 403) {
    // Access forbidden - user doesn't have required role
    console.error('Access denied. User role insufficient for admin operations.');
  }
  return Promise.reject(error);
});

export interface User {
  id: string;
  username: string;
  full_name: string | null;
  email: string;
  role: string;
  is_active: boolean;
  view_only?: boolean;
  two_factor_enabled: boolean;
  created_at: string;
  last_login: string | null;
  supervisor_id?: string | null;
  supervisor_name?: string | null;
  department?: string | null;
}

export interface UserActivity {
  id: string;
  user_id: string;
  action: string;
  details: string;
  created_at: string;
  ip_address: string | null;
}

export interface UserRole {
  value: string;
  label: string;
}

export interface AdminStats {
  total_users: number;
  active_users: number;
  inactive_users: number;
  users_with_2fa: number;
  recent_registrations: number;
}

class AdminDataService {
  private static instance: AdminDataService;
  private users: User[] = [];
  private userActivities: UserActivity[] = [];
  private adminStats: AdminStats | null = null;
  private lastUsersFetch: number = 0;
  private lastActivitiesFetch: number = 0;
  private lastStatsFetch: number = 0;
  private cacheTimeout = 5 * 60 * 1000; // 5 minutes cache

  private constructor() {}

  public static getInstance(): AdminDataService {
    if (!AdminDataService.instance) {
      AdminDataService.instance = new AdminDataService();
    }
    return AdminDataService.instance;
  }

  // User Management API calls
  public async getUsers(forceRefresh: boolean = false): Promise<User[]> {
    const now = Date.now();
    if (!forceRefresh && this.users.length > 0 && (now - this.lastUsersFetch) < this.cacheTimeout) {
      return this.users;
    }

    try {
      const response = await api.get('/admin/users');
      this.users = response.data.users; // Extract users array from response
      this.lastUsersFetch = now;
      return this.users;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch users');
    }
  }

  public async getUserById(userId: string): Promise<User | null> {
    const users = await this.getUsers();
    return users.find(user => user.id === userId) || null;
  }

  public async updateUserRole(userId: string, role: string, isActive?: boolean): Promise<void> {
    try {
      const params: any = { role };
      if (isActive !== undefined) {
        params.is_active = isActive;
      }
      
      await api.patch(`/admin/users/${userId}/role`, {}, { params });
      
      // Update local cache
      const userIndex = this.users.findIndex(user => user.id === userId);
      if (userIndex !== -1) {
        this.users[userIndex].role = role;
        if (isActive !== undefined) {
          this.users[userIndex].is_active = isActive;
        }
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to update user role');
    }
  }

  // Admin only: switch a user's view-only access (sees everything, changes nothing).
  public async setViewOnly(userId: string, viewOnly: boolean): Promise<void> {
    try {
      await api.patch(`/admin/users/${userId}/view-only`, {}, { params: { view_only: viewOnly } });
      const cached = this.users.find(u => u.id === userId);
      if (cached) cached.view_only = viewOnly;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to update view-only access');
    }
  }

  public async toggleUserStatus(userId: string): Promise<void> {
    const user = await this.getUserById(userId);
    if (!user) throw new Error('User not found');

    const newStatus = !user.is_active;
    await this.updateUserRole(userId, user.role, newStatus);
  }

  public async resetUserPassword(userId: string): Promise<{ message: string }> {
    try {
      const response = await api.post('/auth/admin/reset-user-password', {
        user_id: userId
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to reset user password');
    }
  }

  public async deleteUser(userId: string): Promise<void> {
    try {
      await api.delete(`/admin/users/${userId}`);
      
      // Update local cache
      this.users = this.users.filter(user => user.id !== userId);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to delete user';
      throw new Error(errorMessage);
    }
  }

  public async assignSupervisor(userId: string, supervisorId?: string, department?: string): Promise<void> {
    try {
      const params: any = {};
      if (supervisorId) {
        params.supervisor_id = supervisorId;
      }
      if (department) {
        params.department = department;
      }

      await api.post(`/admin/users/${userId}/assign-supervisor`, {}, { params });
      
      // Update local cache
      const userIndex = this.users.findIndex(user => user.id === userId);
      if (userIndex !== -1) {
        this.users[userIndex].supervisor_id = supervisorId || null;
        if (department) {
          this.users[userIndex].department = department;
        }
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to assign supervisor');
    }
  }

  // User Activity Logs
  public async getUserActivities(userId?: string, forceRefresh: boolean = false): Promise<UserActivity[]> {
    const now = Date.now();
    if (!forceRefresh && this.userActivities.length > 0 && (now - this.lastActivitiesFetch) < this.cacheTimeout) {
      return userId ? this.userActivities.filter(activity => activity.user_id === userId) : this.userActivities;
    }

    try {
      const params = userId ? { user_id: userId } : {};
      const response = await api.get('/admin/user-activities', { params });
      this.userActivities = response.data;
      this.lastActivitiesFetch = now;
      return this.userActivities;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch user activities');
    }
  }

  // Admin Statistics
  public async getAdminStats(forceRefresh: boolean = false): Promise<AdminStats> {
    const now = Date.now();
    if (!forceRefresh && this.adminStats && (now - this.lastStatsFetch) < this.cacheTimeout) {
      return this.adminStats;
    }

    try {
      const response = await api.get('/admin/stats');
      this.adminStats = response.data;
      this.lastStatsFetch = now;
      return this.adminStats!;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch admin statistics');
    }
  }

  // Available Roles
  public async getUserRoles(): Promise<UserRole[]> {
    try {
      const response = await api.get('/admin/roles');
      return response.data.roles;
    } catch (error: any) {
      // Always throw error - no frontend fallback, must come from backend
      console.error('Failed to fetch roles from API:', error.message);
      throw new Error(error.response?.data?.detail || 'Failed to load available roles from server');
    }
  }

  // Available Supervisors for a specific role (immediate hierarchy only)
  public async getAvailableSupervisors(role?: string): Promise<User[]> {
    try {
      const response = await api.get('/admin/users', { params: { show_hierarchy: true } });
      
      // Define immediate supervisor roles for each role
      const roleHierarchy: { [key: string]: string[] } = {
        'agent': ['sales_team_lead'],
        'processor_agent': ['processor_team_lead'],
        'sales_team_lead': ['floor_manager'],
        'processor_team_lead': ['floor_manager'],
        'floor_manager': ['director'],
        'director': ['admin'],
        // admin has no supervisor
      };
      
      if (!role || !roleHierarchy[role]) {
        return [];
      }
      
      const allowedSupervisorRoles = roleHierarchy[role];
      const supervisors = response.data.users.filter((user: User) => {
        return allowedSupervisorRoles.includes(user.role) && user.is_active;
      });
      
      return supervisors;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch available supervisors');
    }
  }

  // Cache management
  public invalidateCache(): void {
    this.users = [];
    this.userActivities = [];
    this.adminStats = null;
    this.lastUsersFetch = 0;
    this.lastActivitiesFetch = 0;
    this.lastStatsFetch = 0;
  }

  public async refreshAllData(): Promise<void> {
    await Promise.all([
      this.getUsers(true).then(() => {}),
      this.getUserActivities(undefined, true).then(() => {}),
      this.getAdminStats(true).then(() => {})
    ]);
  }
}

export default AdminDataService;